import { AxiosResponse } from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { mailboxActions } from '../../../reducers/mailboxReducer/mailboxSlice';
import {
  getFolderMessages,
  getFolderMessagesSearchResults,
} from '../../../services/communication/communication.service';
import { RootState } from '../../../store';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import {
  FolderMessageTableItem,
  MessagesFilter,
  MessagesFilterElements,
  MessagesSortById,
  TableMessageParticipant,
} from './MessagesList.types';
import {
  compareFolderMessagesBySentDateAntichronologically,
  compareMessagesInFolderBySentDateChronologically,
  getMessageFlagLabel,
  getMessageTableItem,
  getMonitLabel,
  getTableParticipant,
} from './utils';
import ConversationTable from './ConversationTable.component';
import _ from 'lodash';
import { useStyles } from '../../../pages/styles';
import InfoShield from '../../InfoShield.component';
import { ItemsFilter } from '../../SelectableTable/filters/ItemsFilter.component';
import { DateFilter } from '../../SelectableTable/filters/DateFilter.component';
import { IndexedStrings, SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import MessageFlag from '../MessageFlag.component';
import FilterStateGroup, { LabelWithValue } from '../../../models/filterStateGroup.types';
import { convertToFormatDate, convertToFormatTime } from '../../../utils/dateFunctionUtils';
import {
  Conversation,
  FlagId,
  Folder,
  FolderMessage,
  FolderMessagesResponse,
  foldersWithGroupingDisabled,
  foldersWithUnReadBold,
  MonitId,
  Priority,
} from '../../../models/communication.types';
import { useLayoutContext } from '../../../Context/Layout.context';
import useSessionUser from '../../../hooks/useSessionUser';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import { getToggleButtonStyle } from '../../../pages/SolutionCard/SolutionCard.styles';
import { defaultPageInfo, IPageInfo } from '../../../models/page.types';
import DraftsOutlinedIcon from '@mui/icons-material/DraftsOutlined';
import { activeMessageBackgroundColor } from '../colors';
import { useTranslation } from 'react-i18next';
import { folders, getFolderAndSubFoldersCaptions } from '../helpers';
interface MessagesListProps {
  forcedSession?: string;
  forcedPriority?: Priority;
  forcedIsRead?: boolean;
  searchPhrase?: string;
  onSetFilters?: () => void;
}

const MessagesList: React.FC<MessagesListProps> = (props) => {
  const activeFolder = useSelector((state: RootState) => state.mailbox.activeFolder);

  const { folder, subFolder } = getFolderAndSubFoldersCaptions(activeFolder);

  const activeMessageId = useSelector((state: RootState) => state.mailbox.activeMessageId);
  const loadedMessageContentMessageId = useSelector(
    (state: RootState) => state.mailbox.loadedMessageContent?.messageId,
  );
  const messagesToDelete = useSelector((state: RootState) => state.mailbox.messagesToDelete);
  const messageToUpdateFlag = useSelector((state: RootState) => state.mailbox.messageToUpdateFlag);
  const messagesToInsert = useSelector((state: RootState) => state.mailbox.messagesToInsert);
  const conversationsToMove = useSelector((state: RootState) => state.mailbox.conversationsToMove);
  const dispatch = useDispatch();
  const classes = useStyles();

  const user = useSessionUser();
  const { openAlert, showLoader } = useLayoutContext();
  const { t } = useTranslation();
  const defaultFilter = {
    flag: [],
    sender: '',
    recipient: '',
    participant: '',
    subject: '',
    assessmentSession: '',
    sentDateFrom: '',
    sentDateTo: '',
    monit: [],
    isUnread: false,
  };
  const defaultSortByColumnId: MessagesSortById = 'sendDate';
  const defaultIsDescending = true;

  const getMonitIdForPriority = (priority: Priority) => {
    if (priority === Priority.Medium) return [MonitId.mediumPriority];
    if (priority === Priority.High) return [MonitId.highPriority];
    else return [];
  };

  const [messages, setMessages] = useState<FolderMessage[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [messageTableItems, setMessageTableItems] = useState<FolderMessageTableItem[] | null>(null);
  const [filter, setFilter] = useState<MessagesFilter>(defaultFilter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [filterState, setFilterState] = useState<FilterStateGroup<MessagesFilter>[]>([]);
  const [sortByColumnId, setSortByColumnId] = useState<MessagesSortById>(defaultSortByColumnId);
  const [isDescending, setIsDescending] = useState<boolean>(defaultIsDescending);
  const [pageInfo, setPageInfo] = useState<IPageInfo>(defaultPageInfo);
  const needsToLoadNextMessages = useRef<boolean>(false);
  const [forcedQueryParamsProcessed, setForcedQueryParamsProcessed] = useState<boolean>(false);

  const messagesColumns: SelectableTableColumn[] = [
    {
      id: 'flag',
      type: 'actions',
      title: t('messagebox:messages.table.flag'),
      filterType: 'filter',
      width: '7%',
      sortable: false,
      skipEnterMethod: true,
    },
    {
      id: 'participant',
      type: 'text',
      title: t('messagebox:messages.table.participant'),
      filterType: 'search',
      sortable: false,
      width: '30%',
    },
    {
      id: 'subject',
      type: 'text',
      title: t('messagebox:messages.table.subject'),
      filterType: 'search',
      sortable: true,
      width: '30%',
    },
    {
      id: 'assessmentSession',
      type: 'text',
      title: t('messagebox:messages.table.assessmentSession'),
      filterType: 'search',
      sortable: true,
      width: '20%',
    },
    {
      id: 'monit',
      type: 'text',
      title: t('messagebox:messages.table.monit'),
      filterType: 'filter',
      sortable: false,
      width: '40px',
    },
    {
      id: 'sendDate',
      type: 'text',
      title: t('messagebox:messages.table.sendDate'),
      filterType: 'filter',
      sortable: true,
      width: '13%',
    },
  ];

  const refreshMessages = useCallback(
    _.debounce(
      (
        activeFolder: Folder,
        filter: MessagesFilter,
        sortByColumnId: MessagesSortById | undefined,
        isDescending: boolean,
        pageNumber: number,
        pageSize: number,
        appendItems: boolean,
        searchPhrase: string | undefined,
      ) => {
        const abortController = new AbortController();
        const { signal } = abortController;
        if (!isValidFilter) return;
        showLoader(true);
        const getMessagesFunction: () => Promise<AxiosResponse<FolderMessagesResponse>> = () => {
          if (searchPhrase) {
            return getFolderMessagesSearchResults(
              activeFolder,
              searchPhrase,
              sortByColumnId,
              isDescending,
              pageNumber,
              pageSize,
              true,
              signal,
            );
          } else {
            return getFolderMessages(
              activeFolder,
              filter,
              sortByColumnId,
              isDescending,
              pageNumber,
              pageSize,
              true,
              signal,
            );
          }
        };
        getMessagesFunction()
          .then(
            (response: AxiosResponse<FolderMessagesResponse>) => {
              needsToLoadNextMessages.current = false;
              const messagesItems = response.data.filteredFolderMessages;
              const unreadMessagesItems = messagesItems.filter((e) => e.unreadMessages > 0);
              if (appendItems) {
                setMessages((prevState) =>
                  filter.isUnread ? [...prevState, ...unreadMessagesItems] : [...prevState, ...messagesItems],
                );
              } else {
                setMessages(filter.isUnread ? unreadMessagesItems : messagesItems);
              }

              if (response.data.folderUnreadMessagesIndicatorDto) {
                const folderUnreadMessagesIndicators =
                  response.data.folderUnreadMessagesIndicatorDto.folderUnreadMessagesIndicators;
                dispatch(mailboxActions.updateFolders(folderUnreadMessagesIndicators));
              }
              if (response.data.current) {
                dispatch(mailboxActions.loadMessage(response.data.current));
              } else {
                dispatch(mailboxActions.deActivateMessage());
              }
              setCurrentConversation(response.data.conversation);

              if (response.data.pageInfo) {
                setPageInfo(response.data.pageInfo);
              }
            },
            () => {
              openAlert('get_error');
            },
          )
          .finally(() => {
            dispatch(mailboxActions.setMessageToUpdateFlag(null));
            showLoader(false);
          });
      },
      300,
    ),
    [],
  );

  const isFilterEmpty: boolean = useMemo(() => {
    return _.isEqual(filter, defaultFilter);
  }, [filter]);

  const isSortingDefault: boolean = useMemo(() => {
    return sortByColumnId === defaultSortByColumnId && isDescending === defaultIsDescending;
  }, [sortByColumnId, isDescending]);

  const shouldConversationBeOpen = (message: FolderMessage) => {
    return !!currentConversation?.conversationMessages.some(
      (conversation) => conversation.conversationId === message.conversationId,
    );
  };

  useEffect(() => {
    const tableItems: FolderMessageTableItem[] = messages.map((message: FolderMessage) => {
      if (!message.isGroup) {
        return getMessageTableItem(message, false, foldersWithUnReadBold.includes(activeFolder));
      } else {
        return {
          id: message.messageId,
          flag: null,
          flagId: null,
          participant: null,
          subject: null,
          assessmentSession: null,
          monit: null,
          sendDate: null,
          externalRow: message.isGroup ? (
            <ConversationTable
              message={message}
              conversationId={message.conversationId}
              conversation={
                currentConversation &&
                currentConversation.conversationMessages.length > 0 &&
                currentConversation.conversationMessages[0].conversationId === message.conversationId
                  ? currentConversation
                  : undefined
              }
              open={shouldConversationBeOpen(message)}
              filter={filter}
              sortByColumnId={sortByColumnId}
              isDescending={isDescending}
              isDefaultFilterAndSorting={isSortingDefault && isFilterEmpty}
            />
          ) : null,
        };
      }
    });
    dispatch(mailboxActions.setMessageToUpdateFlag(null));
    setMessageTableItems(tableItems);
  }, [messages, currentConversation, activeFolder, filter, sortByColumnId, isDescending]);

  const refreshActiveFolderMessages = useCallback(() => {
    if (activeFolder === null) return;
    if (!needsToLoadNextMessages.current) {
      setMessageTableItems(null);
    }
    refreshMessages(
      activeFolder,
      filter,
      sortByColumnId,
      isDescending,
      pageInfo.currentPage,
      pageInfo.pageSize,
      needsToLoadNextMessages.current,
      props.searchPhrase,
    );
  }, [activeFolder, filter, sortByColumnId, isDescending, pageInfo.currentPage, pageInfo.pageSize, props.searchPhrase]);

  useEffect(() => {
    refreshActiveFolderMessages();
  }, [refreshActiveFolderMessages]);

  const activateRow = useCallback(
    _.debounce((row: FolderMessageTableItem) => {
      if (row.id === activeMessageId) {
        return;
      }

      const message = messages.find((item) => item.messageId === row.id);
      if (message && !message.isGroup) {
        dispatch(mailboxActions.activateMessage(message.messageId));
      }
    }, 300),
    [messages, activeMessageId],
  );

  const updateFilter = (filterPart: Partial<MessagesFilter>) => {
    setFilter((prevState) => {
      const newState = {
        ...prevState,
        ...filterPart,
      };

      if (!_.isEqual(prevState, newState)) {
        return newState;
      } else {
        return prevState;
      }
    });
  };

  const handleFilterChange = useCallback(
    (name: string, value: unknown) => {
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      let newFilterAttribute: Partial<MessagesFilter> = {};
      if (name === 'participant') {
        const participantInfo = getTableParticipant(activeFolder, user.user, null);
        if (participantInfo === null || participantInfo === TableMessageParticipant.recipient) {
          newFilterAttribute = { ...newFilterAttribute, recipient: value as string };
        }
        if (participantInfo === null || participantInfo === TableMessageParticipant.sender) {
          newFilterAttribute = { ...newFilterAttribute, sender: value as string };
        }
        newFilterAttribute = { ...newFilterAttribute, participant: value as string };
      } else {
        newFilterAttribute = { [name]: value };
      }
      updateFilter(newFilterAttribute);
    },
    [activeFolder, user.user],
  );

  useEffect(() => {
    props.forcedSession && handleFilterChange('assessmentSession', props.forcedSession);
    props.forcedPriority && handleFilterChange('monit', getMonitIdForPriority(props.forcedPriority));
    props.forcedIsRead !== undefined && handleFilterChange('isUnread', !props.forcedIsRead);
    if (props.forcedSession || props.forcedPriority || props.forcedIsRead !== undefined) {
      setForcedQueryParamsProcessed(true);
    }
  }, [props.forcedSession, props.forcedPriority, props.forcedIsRead, handleFilterChange]);

  const filterElements: MessagesFilterElements = {
    flag: (
      <ItemsFilter<FlagId>
        valueName="flag"
        valueLabel="Flaga"
        onChange={handleFilterChange}
        resetFilter={!filter.flag.length}
        itemsWithImages={[FlagId.Green, FlagId.Yellow, FlagId.Red].map((flagId: FlagId) => {
          return {
            text: getMessageFlagLabel(flagId).label,
            value: flagId,
            jsxElement: <MessageFlag flagId={flagId} />,
          };
        })}
        filter={filter.flag}
      />
    ),
    monit: (
      <ItemsFilter<MonitId>
        valueName="monit"
        valueLabel="Monit"
        onChange={handleFilterChange}
        resetFilter={!filter.monit.length}
        itemsWithImages={[MonitId.hasAttachment, MonitId.hasLink, MonitId.mediumPriority, MonitId.highPriority].map(
          (monitId) => {
            const monitData = getMonitLabel(monitId);
            return {
              text: monitData.label,
              value: monitId,
              jsxElement: monitData.icon,
            };
          },
        )}
        filter={filter.monit}
      />
    ),
    sendDate: (
      <DateFilter
        fromValueName="sentDateFrom"
        toValueName="sentDateTo"
        onChange={handleFilterChange}
        resetFilter={filter.sentDateFrom === '' && filter.sentDateTo === ''}
        isActiveFilter={filter.sentDateFrom !== '' || filter.sentDateTo !== ''}
      />
    ),
  };

  const filterTranslated: IndexedStrings = useMemo(() => {
    const result: IndexedStrings = {};
    Object.keys(filter).forEach((key: string) => {
      result[key] = filter[key as keyof MessagesFilter]?.toString() ?? '';
    });
    return result;
  }, [filter]);

  const filterElementsTranslated: { [key: string]: JSX.Element } = {};
  Object.keys(filterElements).forEach((key: string) => {
    filterElementsTranslated[key] = filterElements[key as keyof MessagesFilterElements];
  });

  useEffect(() => {
    const state: FilterStateGroup<MessagesFilter>[] = [];
    if (Object.values(filter).some((e) => e !== '' && e.length > 0)) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.flag.length > 0) {
      state.push({
        name: 'Flaga:',
        propertyName: 'flag',
        checkedItems: filter.flag.map((flagId) => {
          return {
            label: getMessageFlagLabel(flagId).label,
            value: flagId,
          };
        }),
      });
    }

    if (filter.monit.length > 0) {
      state.push({
        name: 'Monit:',
        propertyName: 'monit',
        checkedItems: filter.monit.map((monitId) => {
          return {
            label: getMonitLabel(monitId).label,
            value: monitId,
          };
        }),
      });
    }

    if (filter.sentDateFrom.length > 0) {
      const date = filter.sentDateFrom.toString();
      state.push({
        name: 'Od:',
        propertyName: 'sentDateFrom',
        checkedItems: [`${convertToFormatDate(date)} ${convertToFormatTime(date)}`],
      });
    }

    if (filter.sentDateTo.length > 0) {
      const date = filter.sentDateTo.toString();
      state.push({
        name: 'Do:',
        propertyName: 'sentDateTo',
        checkedItems: [`${convertToFormatDate(date)} ${convertToFormatTime(date)}`],
      });
    }
    setFilterState(state);
  }, [filter]);

  const uncheckFilterItem = (label?: string, propertyName?: keyof MessagesFilter, dataItem?: LabelWithValue) => {
    if (propertyName) {
      setFilter((prevValue) => {
        return {
          ...prevValue,
          [propertyName]:
            Array.isArray(prevValue[propertyName]) && dataItem
              ? (prevValue[propertyName] as Array<unknown>).filter((item) => item !== dataItem.value)
              : '',
        };
      });
    }
  };

  const uncheckAllFilters = () => {
    setFilterState([]);
    setFilter(defaultFilter);
    setMessageTableItems(null);
  };

  const setDefaultFiltrsAndPaging = () => {
    uncheckAllFilters();
    setPageInfo(defaultPageInfo);
  };

  useEffect(() => {
    if (activeFolder === null) return;
    if (!forcedQueryParamsProcessed) {
      //changing of folder via url caused unchecking filters
      setDefaultFiltrsAndPaging();
    }
  }, [activeFolder, forcedQueryParamsProcessed]);

  useEffect(() => {
    if (activeFolder === null) return;
    if (props.searchPhrase !== '') {
      setDefaultFiltrsAndPaging();
    }
  }, [activeFolder, props.searchPhrase]);

  useEffect(() => {
    setPageInfo(defaultPageInfo);
  }, [sortByColumnId, isDescending, filter]);

  useEffect(() => {
    setMessages((prevState) => {
      let readStatusChanged = false;
      const newState = prevState.map((item) => {
        if (
          (item.messageId === activeMessageId || item.messageId === loadedMessageContentMessageId) &&
          item.unreadMessages > 0 &&
          !item.isGroup
        ) {
          readStatusChanged = true;
          return { ...item, unreadMessages: item.unreadMessages - 1 };
        } else {
          return item;
        }
      });
      if (readStatusChanged) {
        dispatch(mailboxActions.decreaseCurrentFolderUnreadMessages(user.user.profile.sub));
      }
      return newState;
    });
  }, [activeMessageId, loadedMessageContentMessageId]);

  const handleIsUnreadChange = (event: React.MouseEvent<HTMLElement>, value: boolean | null) => {
    if (value !== null) {
      handleFilterChange('isUnread', value);
    }
  };

  const handleOnScrollTable = useCallback(
    (endReached: boolean) => {
      if (endReached && pageInfo.hasNext && !needsToLoadNextMessages.current) {
        needsToLoadNextMessages.current = true;
        setPageInfo((prevState) => {
          return { ...prevState, currentPage: prevState.currentPage + 1 };
        });
      }
    },
    [pageInfo.hasNext],
  );

  useEffect(() => {
    if (messagesToDelete.length) {
      refreshActiveFolderMessages();
      dispatch(mailboxActions.removeMessagesToDelete(messagesToDelete));
    }
  }, [messagesToDelete, refreshActiveFolderMessages]);

  useEffect(() => {
    if (conversationsToMove.length) {
      const conversationsFound: string[] = [];
      setMessages((prevState) => {
        const newState = prevState.filter((item) => {
          if (!conversationsToMove.includes(item.conversationId)) {
            return true;
          } else {
            conversationsFound.push(item.conversationId);
          }
        });
        if (newState.length) {
          dispatch(mailboxActions.activateMessage(newState[0].messageId));
        } else {
          dispatch(mailboxActions.deActivateMessage());
        }
        return newState;
      });
      if (conversationsFound.length) {
        dispatch(mailboxActions.removeConversationsToMove(conversationsFound));
      }
    }
  }, [conversationsToMove]);

  useEffect(() => {
    if (messageToUpdateFlag) {
      setMessages((prevState) => {
        const newState = prevState.map((item) => {
          if (item.isGroup || messageToUpdateFlag.messageId === item.messageId) {
            return { ...item, flag: messageToUpdateFlag.flag };
          } else {
            return item;
          }
        });
        return newState;
      });
      dispatch(mailboxActions.setMessageToUpdateFlag(null));
    }
  }, [messageToUpdateFlag]);

  useEffect(() => {
    if (messagesToInsert.length) {
      if (isSortingDefault && isFilterEmpty) {
        setMessages((prevState) => {
          let newState: FolderMessage[] = prevState;
          const messagesToInsertSorted = [...messagesToInsert];
          //sorting to ensure to serve the oldest first
          messagesToInsertSorted.sort(compareMessagesInFolderBySentDateChronologically);

          messagesToInsertSorted.forEach((messageToInsert) => {
            if (messageToInsert.folder !== activeFolder) {
              return;
            }
            const messageWithConversationId = newState.find(
              (item) => item.conversationId === messageToInsert.message.conversationId,
            );
            // message is not in conversation of any other existing message => creating new item
            if (!messageWithConversationId || foldersWithGroupingDisabled.includes(activeFolder)) {
              newState = [
                ...newState,
                {
                  ...messageToInsert.message,
                  isGroup: false,
                  unreadMessages: 1,
                  assessmentSessionId: null,
                  examTaskGroupSolutionScoreId: null,
                },
              ];
            }
            // message is in conversation of existing grouped messages => updating read counter
            else if (messageWithConversationId && messageWithConversationId.isGroup) {
              newState = newState.map((message) => {
                if (message.conversationId === messageToInsert.message.conversationId) {
                  const newHeaderMessage = messageToInsert.message;
                  return {
                    ...message,
                    sentDate: newHeaderMessage.sentDate,
                    unreadMessages: message.unreadMessages + 1,
                  };
                } else {
                  return message;
                }
              });
            }
            // message creates new conversation because one of the existing message has the same conversationId, but is not a group yet
            else if (messageWithConversationId && !messageWithConversationId.isGroup) {
              newState = newState.map((message) => {
                if (message.conversationId === messageWithConversationId.conversationId) {
                  const newHeaderMessage = messageToInsert.message;
                  return {
                    ...message,
                    sentDate: newHeaderMessage.sentDate,
                    unreadMessages: message.unreadMessages + 1,
                    isGroup: true,
                  };
                } else {
                  return message;
                }
              });
            }
          });

          if (newState.length >= 1) {
            newState.sort(compareFolderMessagesBySentDateAntichronologically);
          }

          setTimeout(() => {
            dispatch(mailboxActions.setMessagesToInsertServed(messagesToInsert.map((item) => item.message.messageId)));
          }, 100);
          return newState;
        });
      }
    }
  }, [messagesToInsert, isSortingDefault, isFilterEmpty]);

  useEffect(() => {
    if (!isFilterEmpty && props.onSetFilters) {
      props.onSetFilters();
    }
  }, [isFilterEmpty]);

  return (
    <div
      className={classes.roundedWhiteContener}
      style={{
        height: 'calc(100vh - 150px)',
        margin: '0 12px 12px 0px',
        padding: '5px 0 5px 0',
        backgroundColor:
          messageTableItems !== null && messageTableItems.length === 0 && isFilterEmpty ? '#EBECF4' : undefined,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ height: '100%', overflow: 'hidden' }}>
        {messageTableItems !== null && messageTableItems.length === 0 && isFilterEmpty ? (
          <InfoShield
            icon={
              <DraftsOutlinedIcon
                style={{ fontSize: '80px', color: '#bcbedc', fontWeight: 200, marginBottom: '-12px' }}
              />
            }
            message={
              props.searchPhrase === ''
                ? 'W tej chwili nie posiadasz w skrzynce żadnych wiadomości'
                : 'Nie znaleziono żadnych wiadomości spełniających kryteria wyszukiwania'
            }
          />
        ) : (
          <>
            <div className={classes.toggleButtonGroupContainer}>
              <ToggleButtonGroup
                color="primary"
                exclusive
                size="small"
                fullWidth
                value={filter.isUnread}
                onChange={handleIsUnreadChange}
              >
                <ToggleButton style={getToggleButtonStyle(filter.isUnread === false, true)} value={false}>
                  Wszystkie
                </ToggleButton>
                <ToggleButton style={getToggleButtonStyle(filter.isUnread === true, true)} value={true}>
                  Nieprzeczytane
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <SelectableTable<FolderMessageTableItem, MessagesSortById, MessagesFilter>
              columns={messagesColumns}
              rows={messageTableItems ?? []}
              withColumnWidths={true}
              isMultiChoice={false}
              onRowClick={activateRow}
              filter={filterTranslated}
              filterElements={filterElementsTranslated}
              onFilterChange={handleFilterChange}
              closeFilterItem={uncheckFilterItem}
              uncheckAllFilters={uncheckAllFilters}
              filterbar={filterState}
              sortByProperty={sortByColumnId}
              isDescending={isDescending}
              setIsDescending={setIsDescending}
              setSortByColumn={setSortByColumnId}
              onScrollTable={handleOnScrollTable}
              noOverflowInitial={true}
              maxHeight={'calc(100% - 48px)'} //48px = height of unread messages filter; it should not be present if filter is hidden for example if decided that sent messages could not be unread
              activeRowBackgroundColor={activeMessageBackgroundColor}
              activeRowId={activeMessageId || undefined}
              caption="Skrzynka odbiorcza"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MessagesList;
